/**
 * Amount formatting utilities
 */

/**
 * Formats a number as amount with locale-specific formatting
 * @param amount - The amount to format
 * @param locale - The locale to use for formatting (defaults to 'ro-RO')
 * @returns Formatted amount string
 *
 * @example
 * formatAmount(12000) // "12.000"
 * formatAmount(12000, 'en-US') // "12,000"
 * formatAmount(1500000) // "1.500.000"
 */
export const formatAmount = (
  amount: number,
  locale: string = 'ro-RO'
): string => {
  return amount.toLocaleString(locale);
};

/**
 * Formats a number as amount with specific currency symbol
 * @param amount - The amount to format
 * @param currency - The currency code (e.g., 'lei', 'EUR', 'USD')
 * @param locale - The locale to use for formatting (defaults to 'ro-RO')
 * @returns Formatted amount string with symbol
 *
 * @example
 * formatAmountWithSymbol(12000, 'lei') // "12.000 lei"
 * formatAmountWithSymbol(12000, 'EUR') // "12.000 EUR"
 * formatAmountWithSymbol(12000, 'USD', 'en-US') // "12,000 USD"
 */
export const formatAmountWithSymbol = (
  amount: number,
  currency: string,
  locale: string = 'ro-RO'
): string => {
  const formattedAmount = formatAmount(amount, locale);
  return `${formattedAmount} ${currency}`;
};

/**
 * Formats a number as amount with spaces as thousand separators
 * @param amount - The amount to format
 * @returns Formatted amount string with spaces as separators
 *
 * @example
 * formatAmountWithSpaces(12000) // "12 000"
 * formatAmountWithSpaces(1500000) // "1 500 000"
 * formatAmountWithSpaces(500) // "500"
 */
export const formatAmountWithSpaces = (
  amount: number | undefined,
  precision: number = 2
): string => {
  if (!amount) return '';
  return Number(amount.toFixed(precision))
    .toLocaleString('ro-RO')
    .replace(/\./g, ' ');
};

/**
 * Formats a number as amount with spaces and currency symbol
 * @param amount - The amount to format
 * @param currency - The currency code (e.g., 'lei', 'EUR', 'USD')
 * @returns Formatted amount string with spaces and symbol
 *
 * @example
 * formatAmountWithSpacesAndSymbol(12000, 'lei') // "12 000 lei"
 * formatAmountWithSpacesAndSymbol(12000, 'EUR') // "12 000 EUR"
 * formatAmountWithSpacesAndSymbol(1500000, 'lei') // "1 500 000 lei"
 */
export const formatAmountWithSpacesAndSymbol = (
  amount: number | undefined,
  currency: string,
  precision: number = 2
): string => {
  const formattedAmount = formatAmountWithSpaces(amount, precision);
  return `${formattedAmount} ${currency}`;
};
